import { useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Seo from "@/components/Seo";
import VehiclesGallery from "@/components/VehiclesGallery";
import ReviewsSection from "@/components/ReviewsSection";
import ComparisonSection from "@/components/ComparisonSection";
import ValueStrip from "@/components/ValueStrip";
import ExitIntentModal from "@/components/ExitIntentModal";
import ReferralSection from "@/components/ReferralSection";
import NotFound from "@/pages/NotFound";
import PriceCalculator from "@/components/PriceCalculator";
import FaqSection from "@/components/FaqSection";
import VehicleDetail from "@/pages/VehicleDetail";
import BlogPage from "@/pages/BlogPage";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CookieConsent from "@/components/CookieConsent";
import { LanguageProvider, useLang } from "@/i18n/LanguageContext";
import { VEHICLES } from "@/data/vehicles";
import { useReveal } from "@/hooks/useReveal";
import { AuthProvider } from "@/portal/AuthContext";
import ProtectedRoute from "@/portal/ProtectedRoute";
import LoginPage from "@/portal/LoginPage";
import SetPasswordPage from "@/portal/SetPasswordPage";
import CooperationSignPage from "@/portal/CooperationSignPage";
import { VehiclesProvider } from "@/context/VehiclesContext";
import AdminDashboard from "@/portal/AdminDashboard";
import FlotilaPage from "@/flotila/FlotilaPage";
import FlotilaLoginPage from "@/flotila/FlotilaLoginPage";
import WorkerPage from "@/payroll/WorkerPage";
import ClientPortal from "@/portal/ClientPortal";
import { Toaster } from "@/components/ui/sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ConfirmRoot } from "@/portal/confirm";
import StickyMobileCTA from "@/components/StickyMobileCTA";
import ChatWidget from "@/components/ChatWidget";
import { WhatsAppIcon, InstagramIcon } from "@/components/BrandIcons";
import SpolupracePage from "@/pages/SpolupracePage";
import KarieraPage from "@/pages/KarieraPage";
import {
  Car,
  Phone,
  Mail,
  MapPin,
  Users,
  ChevronDown,
  Menu,
  X,
  Settings,
  FileText,
  Building2,
  User,
  Briefcase,
  ArrowRight,
  CheckCircle2,
  Send,
  Zap,
  Shield,
  Wallet,
  HeartHandshake,
  Handshake,
  Store,
  CarFront,
  Users2,
  Clock,
  Star,
  Download
} from "lucide-react";

// Logo
const LOGO_IMG = "/img/n148kmw9_image.webp";
const LOGO_FULL_IMG = "/effectdeal-logo.webp";
const HERO_BG = "/img/zc0y6d9s_s32.jpg";
const HERO_CAR_IMG = "/img/hero-car.webp";

// Social & contact links
const INSTAGRAM_URL = "https://www.instagram.com/effectdeal";
const WHATSAPP_URL = "https://wa.me/420725706235";

// Social links component (Instagram + WhatsApp brand icons via FontAwesome)
const SocialLinks = ({ className = "" }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <a
      href={INSTAGRAM_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Instagram"
      className="w-10 h-10 rounded-full bg-white/5 hover:bg-orange-500/20 border border-white/10 hover:border-orange-500/40 flex items-center justify-center text-zinc-300 hover:text-white transition-colors"
      data-testid="social-instagram"
    >
      <InstagramIcon className="w-[18px] h-[18px]" />
    </a>
    <a
      href={WHATSAPP_URL}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="WhatsApp"
      className="w-10 h-10 rounded-full bg-white/5 hover:bg-green-500/20 border border-white/10 hover:border-green-500/40 flex items-center justify-center text-zinc-300 hover:text-white transition-colors"
      data-testid="social-whatsapp"
    >
      <WhatsAppIcon className="w-[18px] h-[18px]" />
    </a>
  </div>
);

// Why EffectDeal benefits
const BENEFITS = [
  { icon: HeartHandshake, key: "why.individual" },
  { icon: Zap, key: "why.fast" },
  { icon: Car, key: "why.wide" },
  { icon: Settings, key: "why.flexible" },
  { icon: Wallet, key: "why.prices" }
];

// How it works steps
const STEPS = [
  { number: "1", titleKey: "how.step1Title", descKey: "how.step1Desc" },
  { number: "2", titleKey: "how.step2Title", descKey: "how.step2Desc" },
  { number: "3", titleKey: "how.step3Title", descKey: "how.step3Desc" }
];

// Navigation Component
const Navigation = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useLang();

  const navLinks = [
    { key: "nav.home", href: "#hero" },
    { key: "nav.how", href: "#how-it-works" },
    { key: "nav.why", href: "#why-us" },
    { key: "nav.vehicles", href: "#vehicles" },
    { key: "nav.faq", href: "#faq" },
    { key: "nav.contact", href: "#contact" }
  ];

  // Curated, shorter set for the desktop bar so it never overflows into the logo.
  // The full list above is still shown in the mobile menu.
  const desktopNavKeys = ["nav.home", "nav.how", "nav.why", "nav.vehicles", "nav.faq"];
  const desktopLinks = navLinks.filter((l) => desktopNavKeys.includes(l.key));

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 nav-glass" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16 sm:h-20">
          {/* Logo */}
          <a href="#hero" className="flex items-center gap-2 sm:gap-3" data-testid="logo">
            <img src={LOGO_IMG} alt="EffectDeal" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
            <span className="font-['Cabinet_Grotesk'] font-bold text-lg sm:text-xl tracking-wide">EFFECTDEAL</span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-5 xl:gap-6">
            {desktopLinks.map((link) =>
              <a
                key={link.key}
                href={link.href}
                className="text-sm font-medium text-zinc-400 hover:text-white transition-colors whitespace-nowrap"
                data-testid={`nav-${link.key}`}>
                {t(link.key)}
              </a>
            )}
            <a
              href="/spoluprace"
              className="text-sm font-medium text-orange-400 hover:text-orange-300 transition-colors whitespace-nowrap"
              data-testid="nav-spoluprace">
              Spolupráce
            </a>
            <a
              href="/kariera"
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors whitespace-nowrap"
              data-testid="nav-kariera">
              Kariéra
            </a>
            <a
              href="/blog"
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors whitespace-nowrap"
              data-testid="nav-blog">
              {t("nav.blog")}
            </a>
            <LanguageSwitcher />
            <a
              href="/prihlaseni"
              className="inline-flex items-center gap-1.5 text-sm font-medium text-zinc-200 border border-white/15 hover:border-orange-400/60 hover:text-white rounded-full px-4 py-2 transition-colors whitespace-nowrap"
              data-testid="nav-client-zone">
              <User className="w-4 h-4" /> Klientská zóna
            </a>
            <a
              href="#contact"
              className="btn-accent text-sm px-5 py-2.5"
              data-testid="nav-cta">
              {t("nav.contact")}
            </a>
          </div>

          {/* Mobile controls */}
          <div className="lg:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              className="text-white p-2 -mr-2"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="mobile-menu-btn">
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen &&
          <div className="lg:hidden pb-4 border-t border-white/10 mt-2 pt-4 animate-fade-in-up">
            {navLinks.map((link) =>
              <a
                key={link.key}
                href={link.href}
                className="block py-3 text-zinc-300 hover:text-white transition-colors text-base"
                onClick={() => setIsMenuOpen(false)}>
                {t(link.key)}
              </a>
            )}
            <a
              href="/spoluprace"
              className="block py-3 text-orange-400 hover:text-orange-300 transition-colors text-base font-medium"
              onClick={() => setIsMenuOpen(false)}
              data-testid="nav-spoluprace-mobile">
              Spolupráce
            </a>
            <a
              href="/kariera"
              className="block py-3 text-zinc-300 hover:text-white transition-colors text-base"
              onClick={() => setIsMenuOpen(false)}
              data-testid="nav-kariera-mobile">
              Kariéra
            </a>
            <a
              href="/blog"
              className="block py-3 text-zinc-300 hover:text-white transition-colors text-base"
              onClick={() => setIsMenuOpen(false)}
              data-testid="nav-blog-mobile">
              {t("nav.blog")}
            </a>
            <a
              href="/prihlaseni"
              className="flex items-center justify-center gap-1.5 py-3 mt-3 text-zinc-200 border border-white/15 rounded-full text-base"
              onClick={() => setIsMenuOpen(false)}
              data-testid="nav-client-zone-mobile">
              <User className="w-4 h-4" /> Klientská zóna
            </a>
            <a
              href="#contact"
              className="btn-accent text-sm px-6 py-3 mt-3 inline-block w-full text-center"
              onClick={() => setIsMenuOpen(false)}>
              {t("cta.inquiry")}
            </a>
          </div>
        }
      </div>
    </nav>);
};

// Hero Section
const HeroSection = () => {
  const { t } = useLang();
  const carRef = useRef(null);

  const handleParallax = (e) => {
    const el = carRef.current;
    if (!el || !window.matchMedia?.("(hover: hover)").matches) return;
    const px = e.clientX / window.innerWidth - 0.5;
    const py = e.clientY / window.innerHeight - 0.5;
    el.style.transform = `translate3d(${(-px * 22).toFixed(1)}px, ${(-py * 16).toFixed(1)}px, 0) scale(1.1)`;
  };

  return (
    <section id="hero" className="hero-section" data-testid="hero-section" onMouseMove={handleParallax}>
      {/* Animated car background */}
      <div
        ref={carRef}
        className="hero-car-bg"
        style={{ backgroundImage: `url(${HERO_CAR_IMG})` }}
        data-testid="hero-car-bg"
      />
      <div className="hero-sweep" />
      <div className="hero-glow" />

      <div className="hero-overlay" />

      <div className="relative z-10 text-center px-4 sm:px-6 max-w-5xl mx-auto pt-20 sm:pt-24 mt-16 sm:mt-20">
        {/* Brand logo (transparent PNG) overlaid on building background */}
        <img
          src={LOGO_FULL_IMG}
          alt="EffectDeal logo"
          className="w-40 sm:w-72 lg:w-80 h-auto mx-auto mb-2 animate-fade-in-up drop-shadow-2xl"
          data-testid="hero-logo"
        />
        <p className="text-white font-['Cabinet_Grotesk'] font-bold text-xl sm:text-3xl lg:text-4xl mb-3 sm:mb-4 animate-fade-in-up animation-delay-100 drop-shadow-lg" data-testid="hero-motto">
          Levnější než půjčovna. <span className="text-orange-400">Flexibilnější než operák.</span>
        </p>
        <p className="text-orange-400 font-semibold text-base sm:text-lg lg:text-xl mb-4 sm:mb-6 animate-fade-in-up animation-delay-100 drop-shadow-lg">
          {t("hero.subtitle")}
        </p>
        <p className="text-zinc-200 text-sm sm:text-lg max-w-3xl mx-auto mb-2 animate-fade-in-up animation-delay-200 drop-shadow-md">
          {t("hero.line1")}
        </p>
        <p className="text-zinc-400 text-xs sm:text-base max-w-2xl mx-auto mb-8 sm:mb-10 animate-fade-in-up animation-delay-200">
          {t("hero.line2")}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 animate-fade-in-up animation-delay-300">
          <a href="#contact" className="btn-accent w-full sm:w-auto" data-testid="hero-cta-contact">
            {t("cta.inquiry")}
          </a>
          <a href="#how-it-works" className="btn-outline w-full sm:w-auto" data-testid="hero-cta-how">
            {t("hero.ctaHow")}
          </a>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-7 sm:mt-9 text-xs sm:text-sm text-zinc-300 animate-fade-in-up animation-delay-300" data-testid="hero-trust-bar">
          <span className="inline-flex items-center gap-1.5"><Star className="w-4 h-4 text-orange-400 fill-orange-400" /> 5,0 na Googlu</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Bez dlouhých závazků</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Vše v ceně, bez skrytých poplatků</span>
          <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Doručíme kamkoliv v ČR</span>
        </div>
      </div>

      {/* Scroll indicator - hidden on mobile */}
      <div className="hidden sm:block absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <ChevronDown className="w-8 h-8 text-zinc-500" />
      </div>
    </section>);
};

// How It Works Section
const HowItWorksSection = () => {
  const { t } = useLang();
  return (
    <section id="how-it-works" className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0a0a0a]" data-testid="how-it-works-section">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10 sm:mb-16 reveal">
          <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4">
            <Settings className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{t("how.eyebrow")}</span>
          </div>
          <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">{t("how.title")}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 reveal reveal-delay-1">
          {STEPS.map((step, index) =>
            <div
              key={step.number}
              className="relative"
              data-testid={`step-${step.number}`}>

              <div className="glass p-6 sm:p-8 rounded-2xl h-full">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-xl sm:text-2xl font-bold text-black mb-4 sm:mb-6">
                  {step.number}
                </div>
                <h3 className="font-['Cabinet_Grotesk'] text-lg sm:text-xl font-bold mb-2 sm:mb-3">{t(step.titleKey)}</h3>
                <p className="text-zinc-400 text-sm sm:text-base">{t(step.descKey)}</p>
              </div>
              {index < STEPS.length - 1 &&
                <div className="hidden md:block absolute top-1/2 -right-3 lg:-right-4 transform -translate-y-1/2 z-10">
                  <ArrowRight className="w-6 h-6 lg:w-8 lg:h-8 text-orange-500/50" />
                </div>
              }
            </div>
          )}
        </div>

        <div className="text-center mt-8 sm:mt-12 space-y-1 sm:space-y-2">
          <p className="text-base sm:text-lg text-zinc-300 font-medium">
            {t("how.footer1")}
          </p>
          <p className="text-base sm:text-lg text-orange-400 font-medium">
            {t("how.footer2")}
          </p>
        </div>
      </div>
    </section>);
};

// Proč EffectDeal – sloučené "Co nabízíme" + "Proč my" + "Pro koho" do jedné krátké sekce
const WhyEffectDealSection = () => {
  const { t } = useLang();
  return (
    <section id="why-us" className="py-16 sm:py-24 px-4 sm:px-6 relative" data-testid="why-us-section">
      <div className="gradient-radial absolute inset-0 pointer-events-none" />
      <div className="max-w-6xl mx-auto relative">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-start">
          <div className="reveal">
            <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>{t("why.eyebrow")}</span>
            </div>
            <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl !text-left">{t("why.title")}</h2>
            <p className="text-zinc-300 text-base sm:text-lg leading-relaxed mt-5" data-testid="why-intro">{t("why.p")}</p>

            <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-8" data-testid="why-stats">
              {[
                { v: "ihned", l: "Schválení a vyřízení" },
                { v: "od 1 měsíce", l: "Bez dlouhých závazků" },
                { v: "v ceně", l: "Pojištění, servis i známka" },
                { v: "5,0★", l: "Hodnocení na Googlu" },
              ].map((s) => (
                <div key={s.l} className="glass rounded-2xl p-4 sm:p-5">
                  <p className="font-['Cabinet_Grotesk'] font-bold text-2xl sm:text-3xl text-orange-400">{s.v}</p>
                  <p className="text-xs sm:text-sm text-zinc-400 mt-1">{s.l}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal reveal-delay-1">
            <div className="grid grid-cols-1 gap-2.5">
              {BENEFITS.map((benefit, index) => (
                <div key={benefit.key} className="flex items-center gap-3 sm:gap-4 p-3.5 sm:p-4 glass rounded-2xl hover:border-orange-500/30 transition-colors" data-testid={`benefit-${index}`}>
                  <div className="w-10 h-10 rounded-xl bg-orange-500/15 flex items-center justify-center flex-shrink-0">
                    <benefit.icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="font-medium text-sm sm:text-base text-zinc-200">{t(benefit.key)}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 rounded-2xl border border-white/10 bg-white/[0.03] p-4 flex items-start gap-3" data-testid="why-for">
              <Users className="w-5 h-5 text-orange-400 shrink-0 mt-0.5" />
              <div>
                <p className="text-[11px] uppercase tracking-wider text-zinc-500">{t("why.forTitle")}</p>
                <p className="text-sm text-zinc-300 mt-1">{t("why.for")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);
};

// Partnership Section
const PartnershipSection = () => {
  const { t } = useLang();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    vehicleCount: '',
    vehicleTypes: '',
    contact: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const partners = [
    { icon: Store, key: "partner.bazars" },
    { icon: Car, key: "partner.rentals" },
    { icon: CarFront, key: "partner.fleet" },
    { icon: Users2, key: "partner.owners" }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.vehicleCount) { alert(t("part.select")); return; }
    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/partnership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Nepodařilo se odeslat');
      }

      setShowSuccess(true);
      setShowForm(false);
      setFormData({ company: '', vehicleCount: '', vehicleTypes: '', contact: '' });
    } catch (err) {
      alert(t("part.error"));
      console.error('Partnership form error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const SuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in-up" onClick={() => setShowSuccess(false)}>
      <div className="bg-[#121212] border border-orange-500/30 rounded-2xl p-8 sm:p-10 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
          <Handshake className="w-10 h-10 text-white" />
        </div>
        <h3 className="font-['Cabinet_Grotesk'] text-2xl sm:text-3xl font-bold mb-3">{t("part.successTitle")}</h3>
        <p className="text-zinc-400 mb-6">{t("part.successDesc")}</p>
        <button
          onClick={() => setShowSuccess(false)}
          className="bg-orange-400 hover:bg-orange-500 text-black py-3 px-8 rounded-full font-bold transition-all w-full"
        >
          {t("part.ok")}
        </button>
      </div>
    </div>
  );

  return (
    <section id="partnership" className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0a0a0a]" data-testid="partnership-section">
      {showSuccess && <SuccessModal />}

      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-10 sm:mb-12 reveal">
          <div className="inline-flex items-center gap-2 text-orange-500 font-medium tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4">
            <Handshake className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{t("part.eyebrow")}</span>
          </div>
          <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">{t("part.title")}</h2>
        </div>

        <div className="glass p-6 sm:p-10 rounded-2xl border-orange-500/20">
          <div className="text-center mb-8">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">{t("part.q")}</h3>
            <p className="text-zinc-400 text-sm sm:text-base">{t("part.sub")}</p>
          </div>

          <div className="mb-8">
            <p className="text-center text-zinc-500 text-sm mb-4">{t("part.with")}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
              {partners.map((partner, index) => (
                <div
                  key={partner.key}
                  className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 hover:bg-orange-500/10 transition-colors"
                  data-testid={`partner-${index}`}
                >
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                    <partner.icon className="w-5 h-5 text-orange-400" />
                  </div>
                  <span className="text-xs sm:text-sm text-zinc-300 text-center">{t(partner.key)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="text-center space-y-4">
            <p className="text-zinc-400 text-sm sm:text-base">{t("part.text1")}</p>
            <p className="text-zinc-300 text-sm sm:text-base">{t("part.text2")}</p>
          </div>

          {/* Partner Form Toggle */}
          {!showForm ? (
            <div className="mt-8 text-center">
              <button
                onClick={() => setShowForm(true)}
                className="bg-orange-400 hover:bg-orange-500 text-black py-3 px-8 rounded-full font-bold transition-all inline-flex items-center gap-2"
                data-testid="show-partner-form-btn"
              >
                <Handshake className="w-5 h-5" />
                {t("part.interestBtn")}
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-8 p-5 sm:p-6 rounded-xl bg-orange-500/5 border border-orange-500/20" data-testid="partner-form">
              <h4 className="text-lg font-bold text-white mb-5 flex items-center gap-2">
                <Handshake className="w-5 h-5 text-orange-500" />
                {t("part.formTitle")}
              </h4>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5">{t("part.company")}</label>
                  <input
                    type="text"
                    className="input-dark text-base"
                    placeholder={t("part.companyPlaceholder")}
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    required
                    disabled={isSubmitting}
                    data-testid="partner-input-company"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5">{t("part.count")}</label>
                    <Select value={formData.vehicleCount} onValueChange={(v) => setFormData({ ...formData, vehicleCount: v })} disabled={isSubmitting}>
                      <SelectTrigger className="input-dark text-base h-auto" data-testid="partner-input-count">
                        <SelectValue placeholder={t("part.select")} />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0a0a0a] border-white/10 text-zinc-200">
                        <SelectItem value="1-3">{t("part.count1")}</SelectItem>
                        <SelectItem value="4-10">{t("part.count2")}</SelectItem>
                        <SelectItem value="11-20">{t("part.count3")}</SelectItem>
                        <SelectItem value="20+">{t("part.count4")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5">{t("part.types")}</label>
                    <input
                      type="text"
                      className="input-dark text-base"
                      placeholder={t("part.typesPlaceholder")}
                      value={formData.vehicleTypes}
                      onChange={(e) => setFormData({ ...formData, vehicleTypes: e.target.value })}
                      disabled={isSubmitting}
                      data-testid="partner-input-types"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5">{t("part.contact")}</label>
                  <input
                    type="text"
                    className="input-dark text-base"
                    placeholder={t("part.contactPlaceholder")}
                    value={formData.contact}
                    onChange={(e) => setFormData({ ...formData, contact: e.target.value })}
                    required
                    disabled={isSubmitting}
                    data-testid="partner-input-contact"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <button
                  type="submit"
                  className="bg-orange-400 hover:bg-orange-500 text-black py-3 px-6 rounded-full font-bold transition-all flex-1 flex items-center justify-center gap-2 disabled:opacity-50"
                  disabled={isSubmitting}
                  data-testid="partner-submit-btn"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t("part.submitting")}
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      {t("part.submit")}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="py-3 px-6 rounded-full font-medium text-zinc-400 hover:text-white transition-colors"
                >
                  {t("part.cancel")}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const { t } = useLang();
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', vehicleType: '', duration: '', message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Nepodařilo se odeslat poptávku');
      }

      setShowSuccess(true);
      setFormData({ name: '', email: '', phone: '', vehicleType: '', duration: '', message: '' });
    } catch (err) {
      setError(t("contact.error"));
      console.error('Form submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const SuccessModal = () => (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fade-in-up" onClick={() => setShowSuccess(false)}>
      <div className="bg-[#121212] border border-white/10 rounded-2xl p-8 sm:p-10 max-w-md w-full text-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center">
          <CheckCircle2 className="w-10 h-10 text-white" />
        </div>
        <h3 className="font-['Cabinet_Grotesk'] text-2xl sm:text-3xl font-bold mb-3">{t("contact.successTitle")}</h3>
        <p className="text-zinc-400 mb-6">{t("contact.successDesc")}</p>
        <div className="glass p-4 rounded-xl mb-6 text-left">
          <div className="flex items-center gap-3 text-sm">
            <Mail className="w-5 h-5 text-orange-400 flex-shrink-0" />
            <span className="text-zinc-300">{t("contact.successEmail")}</span>
          </div>
        </div>
        <button
          onClick={() => setShowSuccess(false)}
          className="btn-primary w-full"
          data-testid="close-success-modal"
        >
          {t("contact.ok")}
        </button>
      </div>
    </div>
  );

  return (
    <section id="contact" className="py-16 sm:py-24 px-4 sm:px-6 bg-[#0a0a0a]" data-testid="contact-section">
      {showSuccess && <SuccessModal />}

      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-8 sm:mb-12 reveal">
          <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-3 sm:mb-4">
            <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
            <span>{t("contact.eyebrow")}</span>
          </div>
          <h2 className="section-title text-2xl sm:text-3xl lg:text-4xl">{t("contact.title")}</h2>
          <p className="text-zinc-500 max-w-xl mx-auto mt-3 sm:mt-4 text-sm sm:text-base">
            {t("contact.sub")}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="glass p-5 sm:p-8 rounded-2xl" data-testid="contact-form">
          {error && (
            <div className="mb-5 p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-4 sm:space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div>
                <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5 sm:mb-2">{t("contact.name")}</label>
                <input
                  type="text"
                  className="input-dark text-base"
                  placeholder={t("contact.namePlaceholder")}
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  disabled={isSubmitting}
                  data-testid="input-name" />
              </div>
              <div>
                <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5 sm:mb-2">{t("contact.email")}</label>
                <input
                  type="email"
                  className="input-dark text-base"
                  placeholder={t("contact.emailPlaceholder")}
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  disabled={isSubmitting}
                  data-testid="input-email" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
              <div>
                <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5 sm:mb-2">{t("contact.phone")}</label>
                <input
                  type="tel"
                  className="input-dark text-base"
                  placeholder={t("contact.phonePlaceholder")}
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  disabled={isSubmitting}
                  data-testid="input-phone" />
              </div>
              <div>
                <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5 sm:mb-2">{t("contact.vehicleType")}</label>
                <Select value={formData.vehicleType} onValueChange={(v) => setFormData({ ...formData, vehicleType: v })} disabled={isSubmitting}>
                  <SelectTrigger className="input-dark text-base h-auto" data-testid="input-vehicle-type">
                    <SelectValue placeholder={t("contact.select")} />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0a0a0a] border-white/10 text-zinc-200 max-h-72">
                    {VEHICLES.map((v) => (
                      <SelectItem key={v.slug} value={v.name}>{v.name}</SelectItem>
                    ))}
                    <SelectItem value="other">{t("vt.other")}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5 sm:mb-2">{t("contact.duration")}</label>
              <Select value={formData.duration} onValueChange={(v) => setFormData({ ...formData, duration: v })} disabled={isSubmitting}>
                <SelectTrigger className="input-dark text-base h-auto" data-testid="input-duration">
                  <SelectValue placeholder={t("contact.select")} />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-white/10 text-zinc-200">
                  <SelectItem value="1-3">{t("dur.1-3")}</SelectItem>
                  <SelectItem value="3-6">{t("dur.3-6")}</SelectItem>
                  <SelectItem value="6-12">{t("dur.6-12")}</SelectItem>
                  <SelectItem value="12+">{t("dur.12+")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-xs sm:text-sm text-zinc-400 mb-1.5 sm:mb-2">{t("contact.requirements")}</label>
              <textarea
                className="input-dark min-h-[100px] sm:min-h-[120px] resize-none text-base"
                placeholder={t("contact.requirementsPlaceholder")}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                disabled={isSubmitting}
                data-testid="input-message" />
            </div>
          </div>

          <button
            type="submit"
            className="btn-accent w-full flex items-center justify-center gap-2 mt-5 sm:mt-6 text-base disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={isSubmitting}
            data-testid="submit-btn">
            {isSubmitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t("contact.submitting")}
              </>
            ) : (
              <>
                <Send className="w-4 h-4 sm:w-5 sm:h-5" />
                {t("contact.submit")}
              </>
            )}
          </button>
        </form>

        {/* Contact Info */}
        <div className="mt-8 sm:mt-12 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <a href="mailto:marketing@effectdeal.com" className="glass p-4 sm:p-5 rounded-xl flex items-center gap-3 sm:gap-4 hover:border-orange-500/30 transition-colors" data-testid="contact-info-email">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <Mail className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-zinc-500">{t("contact.infoEmail")}</p>
              <p className="text-zinc-300 text-sm leading-snug [overflow-wrap:anywhere]">marketing@effectdeal.com</p>
            </div>
          </a>

          <a href="tel:+420725706235" className="glass p-4 sm:p-5 rounded-xl flex items-center gap-3 sm:gap-4 hover:border-orange-500/30 transition-colors" data-testid="contact-info-phone">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <Phone className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-zinc-500">{t("contact.infoPhone")}</p>
              <p className="text-zinc-300 text-sm sm:text-base">+420 725 706 235</p>
            </div>
          </a>

          <div className="glass p-4 sm:p-5 rounded-xl flex items-center gap-3 sm:gap-4" data-testid="contact-info-location">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <MapPin className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-zinc-500">{t("contact.infoArea")}</p>
              <p className="text-zinc-300 text-sm sm:text-base">{t("contact.areaValue")}</p>
            </div>
          </div>

          <div className="glass p-4 sm:p-5 rounded-xl flex items-center gap-3 sm:gap-4" data-testid="contact-info-hours">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg bg-orange-500/20 flex items-center justify-center flex-shrink-0">
              <Clock className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-zinc-500">{t("contact.infoHours")}</p>
              <p className="text-zinc-300 text-sm sm:text-base">24/7</p>
            </div>
          </div>
        </div>

        {/* Social links */}
        <div className="mt-6 flex items-center justify-center gap-3" data-testid="contact-socials">
          <SocialLinks />
        </div>
      </div>
    </section>);
};

// Footer
export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="py-8 sm:py-12 px-4 sm:px-6 border-t border-white/5" data-testid="footer">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col items-center gap-6 sm:gap-8">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src={LOGO_IMG} alt="EffectDeal" className="w-8 h-8 object-contain" />
            <span className="font-['Cabinet_Grotesk'] font-bold tracking-wide">EFFECTDEAL</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
            <a href="#why-us" className="footer-link">{t("nav.why")}</a>
            <a href="#how-it-works" className="footer-link">{t("footer.how")}</a>
            <a href="#vehicles" className="footer-link">{t("footer.vehicles")}</a>
            <a href="#faq" className="footer-link">{t("nav.faq")}</a>
            <a href="/blog" className="footer-link" data-testid="footer-blog-link">{t("nav.blog")}</a>
            <a href="/spoluprace" className="footer-link">{t("footer.partnership")}</a>
            <a href="/kariera" className="footer-link" data-testid="footer-kariera">Kariéra</a>
            <a href="#contact" className="footer-link">{t("footer.contact")}</a>
          </div>

          <div className="flex items-center gap-4 text-xs text-zinc-500">
            <a href="/vop" className="hover:text-zinc-300 transition-colors">{t("vop.eyebrow")}</a>
            <span>|</span>
            <a href="/gdpr" className="hover:text-zinc-300 transition-colors">{t("gdpr.eyebrow")}</a>
            <span>|</span>
            <a href="/prihlaseni" className="hover:text-zinc-300 transition-colors" data-testid="footer-login-link">Klientská zóna</a>
          </div>

          <SocialLinks />

          <p className="text-zinc-600 text-xs sm:text-sm text-center">
            © {new Date().getFullYear()} EffectDeal · IČO: 24952923. {t("common.rights")}
          </p>
        </div>
      </div>
    </footer>);
};

// VOP — provider identification + full document (effective 30. 10. 2026)
const VOP_PROVIDER = [
  ["IČO", "24952923"],
  ["DIČ", "CZ24952923 (plátce DPH)"],
  ["Sídlo", "Školská 660/3, Nové Město, 110 00 Praha 1"],
  ["E-mail", "daniel@effectdeal.com"],
  ["Telefon", "+420 725 706 235"],
  ["Web", "www.effectdeal.com"],
];
const VOP_ARTICLES = [
  { title: "Úvodní ustanovení", items: [
    "Pronajímatel poskytuje služby spočívající zejména v pronájmu motorových vozidel a souvisejícím zajištění jejich dostupnosti. Tyto VOP se použijí na krátkodobé i dlouhodobé pronájmy; standardní minimální doba pronájmu činí 30 kalendářních dnů, není-li individuálně sjednáno jinak.",
    "Nájemcem může být spotřebitel, podnikající fyzická osoba nebo právnická osoba. Spotřebitelem je fyzická osoba, která uzavírá smlouvu mimo rámec své podnikatelské činnosti nebo samostatného výkonu povolání.",
    "Pronajímatel může pronajímat vozidla, která vlastní, užívá na základě smluvního oprávnění nebo která je oprávněn dále přenechat k užívání. Vůči Nájemci vystupuje jako smluvní strana v rozsahu sjednaném v nájemní smlouvě. Vlastnické právo k vozidlu nemusí náležet Pronajímateli.",
    "Právní vztahy se řídí právním řádem České republiky, zejména zákonem č. 89/2012 Sb., občanským zákoníkem, a příslušnými předpisy na ochranu spotřebitele.",
  ]},
  { title: "Uzavření smlouvy a smluvní dokumentace", items: [
    "Nájemní vztah vzniká uzavřením individuální nájemní smlouvy. Smlouva může být uzavřena v listinné nebo elektronické podobě, včetně prostřednictvím klientské zóny Pronajímatele.",
    "Smluvní dokumentaci tvoří nájemní smlouva, tyto VOP, předávací protokol a přílohy nebo sazebníky, na které nájemní smlouva výslovně odkazuje a které byly Nájemci zpřístupněny před uzavřením smlouvy.",
    "V případě rozporu má přednost individuální ujednání nájemní smlouvy, ledaže by tím byla porušena kogentní ustanovení právních předpisů nebo práva spotřebitele.",
    "Nájemce poskytne pravdivé a úplné údaje potřebné k uzavření smlouvy. Pronajímatel může přiměřeně ověřit totožnost Nájemce, platnost jeho řidičského oprávnění a další skutečnosti významné pro bezpečné a řádné plnění smlouvy.",
    "Pronajímatel může odmítnout uzavření smlouvy zejména tehdy, pokud existují důvodné pochybnosti o pravdivosti poskytnutých údajů, oprávnění Nájemce vozidlo řídit nebo schopnosti plnit sjednané povinnosti.",
  ]},
  { title: "Předání a převzetí vozidla", items: [
    "Vozidlo se předává v termínu a na místě sjednaném v nájemní smlouvě. O předání se vyhotoví předávací protokol, který je součástí smluvní dokumentace.",
    "Předávací protokol zachycuje zejména identifikaci vozidla, registrační značku a VIN, stav tachometru, stav paliva nebo nabití baterie, popis stavu vozidla, zaznamenaná poškození a předané příslušenství. Součástí dokumentace může být fotodokumentace.",
    "Nájemce je povinen vozidlo při převzetí přiměřeně prohlédnout a upozornit na zjevné nedostatky. Podpisem protokolu potvrzuje převzetí ve stavu v něm zaznamenaném; tím nejsou dotčena práva z vad, které nebylo možné při běžné prohlídce zjistit.",
    "Pronajímatel předá vozidlo ve stavu způsobilém k obvyklému a bezpečnému užívání a v souladu s nájemní smlouvou a právními předpisy.",
  ]},
  { title: "Nájemné a platební podmínky", items: [
    "Výše nájemného, splatnost a způsob úhrady jsou sjednány v nájemní smlouvě. Nájemné je zpravidla sjednáno jako měsíční platba.",
    "Nájemce hradí nájemné na bankovní účet uvedený v nájemní smlouvě nebo na řádném platebním dokladu. Není-li sjednáno jinak, je nájemné splatné předem za příslušné nájemní období.",
    "Pronajímatel může zasílat faktury a jiné platební doklady elektronicky na kontaktní e-mail Nájemce.",
    "V případě prodlení má Pronajímatel právo požadovat zákonný úrok z prodlení a další nároky, které mu náleží podle právních předpisů a platných smluvních ujednání.",
    "Daňový režim nájemného a dalších plateb se řídí právními předpisy a daňovým statusem Pronajímatele v době uskutečnění příslušného plnění.",
  ]},
  { title: "Kauce", items: [
    "Pronajímatel může požadovat složení peněžité jistoty (dále jen „Kauce“) před předáním vozidla. Výše Kauce je sjednána individuálně v nájemní smlouvě.",
    "Kauce zajišťuje pohledávky Pronajímatele vzniklé v souvislosti s nájemním vztahem, zejména dlužné nájemné, náhradu škody, sjednanou spoluúčast, náklady na odstranění poškození, chybějící příslušenství a jiné splatné a oprávněné pohledávky.",
    "Pronajímatel může proti Kauci započíst své splatné pohledávky v souladu s právními předpisy. Pokud byla sjednána povinnost doplnit čerpanou Kauci, Nájemce ji doplní ve sjednané lhůtě.",
    "Po skončení nájmu Pronajímatel provede vyúčtování Kauce a vrátí její nespornou část bez zbytečného odkladu. Nelze-li některý nárok při vrácení vozidla objektivně vyčíslit, může Pronajímatel po nezbytně nutnou dobu zadržet pouze přiměřenou část odpovídající důvodně předpokládanému nároku a Nájemci sdělí důvod zadržení.",
    "Kauce není nájemným ani smluvní pokutou.",
  ]},
  { title: "Užívání vozidla a povinnosti Nájemce", items: [
    "Nájemce je povinen užívat vozidlo řádně, šetrně, v souladu s jeho technickým určením, právními předpisy a nájemní smlouvou.",
    "Nájemce zejména dodržuje pravidla silničního provozu, zabezpečuje vozidlo proti odcizení, sleduje výstražná hlášení, přiměřeně kontroluje provozní kapaliny a tlak v pneumatikách a bez zbytečného odkladu oznamuje závady, poškození a jiné události, které mohou ovlivnit bezpečnost nebo provozuschopnost vozidla.",
    "Bez předchozího souhlasu Pronajímatele nesmí Nájemce vozidlo dále pronajímat nebo přenechat k podnikatelskému užívání třetí osobě, používat je k taxislužbě či obdobné přepravní činnosti, provádět technické úpravy, odstraňovat zabezpečovací prvky nebo je používat k závodům, soutěžím či výcviku řidičů, pokud nájemní smlouva nestanoví jinak.",
    "Kouření ve vozidle je zakázáno, pokud není v nájemní smlouvě výslovně sjednáno jinak.",
    "Vozidlo mohou řídit pouze osoby s platným řidičským oprávněním a splňující podmínky nájemní smlouvy. Nájemce odpovídá za osoby, kterým umožní vozidlo užívat, v rozsahu stanoveném právními předpisy a smlouvou.",
    "Užívání vozidla mimo území Evropské unie vyžaduje předchozí souhlas Pronajímatele, není-li sjednáno jinak. Nájemce je povinen dodržet také územní omezení vyplývající z pojištění.",
  ]},
  { title: "Kilometrový limit", items: [
    "Kilometrový limit může být sjednán jako měsíční nebo celkový a jeho výše je uvedena v nájemní smlouvě.",
    "Za překročení limitu může být účtována částka za každý nadlimitní kilometr podle sazby uvedené v nájemní smlouvě nebo v předem zpřístupněném sazebníku.",
    "Stav tachometru se zaznamenává při předání a vrácení vozidla; v průběhu nájmu může být přiměřeně ověřen po předchozí dohodě s Nájemcem.",
  ]},
  { title: "Pojištění a spoluúčast", items: [
    "Vozidlo je pojištěno v rozsahu sjednaném pro konkrétní vozidlo. Rozsah pojištění, výše spoluúčasti a podstatná omezení pojistného krytí se uvádějí v nájemní smlouvě nebo v jejích přílohách.",
    "Pojištění nezaručuje, že každá škoda vzniklá během nájmu bude pojišťovnou uhrazena. Nájemce odpovídá za škodu v rozsahu stanoveném právními předpisy, nájemní smlouvou a použitelnými pojistnými podmínkami.",
    "Spoluúčast Nájemce se uplatní pouze v rozsahu platného smluvního ujednání a příslušného pojistného krytí. Není-li škoda pojištěním kryta, posoudí se odpovědnost Nájemce podle právních předpisů a nájemní smlouvy.",
  ]},
  { title: "Dopravní nehody a škodní události", items: [
    "Při nehodě, poškození, odcizení nebo jiné škodní události Nájemce bez zbytečného odkladu kontaktuje Pronajímatele a postupuje podle právních předpisů a pokynů příslušných orgánů.",
    "Nájemce zejména zajistí bezpečnost osob a místa události, přivolá policii v zákonem stanovených případech, pořídí dostupnou fotodokumentaci, zajistí potřebné údaje dalších účastníků a vyplní záznam o dopravní nehodě, je-li to potřebné.",
    "Nájemce poskytne pravdivé a úplné informace a potřebnou součinnost při šetření a likvidaci pojistné události.",
    "Bez předchozího souhlasu Pronajímatele Nájemce neobjedná opravu vozidla, s výjimkou neodkladného zásahu nutného k zajištění bezpečnosti nebo zabránění vzniku další škody.",
  ]},
  { title: "Servis a údržba", items: [
    "Nájemce dodržuje servisní intervaly a pokyny výrobce. Rozdělení povinností a nákladů na servis, údržbu, pneumatiky, opravy a provozní výdaje se řídí nájemní smlouvou.",
    "Nájemce bez zbytečného odkladu oznámí technické závady, výstražná hlášení a skutečnosti, které mohou ovlivnit bezpečnost nebo provozuschopnost vozidla.",
    "Bez souhlasu Pronajímatele Nájemce neobjednává opravy ani servisní práce, s výjimkou neodkladných úkonů nezbytných k odvrácení škody nebo ohrožení bezpečnosti.",
  ]},
  { title: "GPS a zabezpečovací zařízení", items: [
    "Vozidlo může být vybaveno GPS lokalizačním zařízením nebo jiným zabezpečovacím systémem. Účelem může být zejména ochrana vozidla před odcizením, jeho vyhledání, ochrana majetkových práv Pronajímatele a řešení škodních událostí.",
    "Rozsah údajů, právní titul zpracování, doba uchovávání a práva subjektů údajů jsou popsány v samostatných Zásadách zpracování osobních údajů, které musí být Nájemci zpřístupněny.",
    "Nájemce nesmí úmyslně odpojovat, poškozovat nebo omezovat funkčnost zabezpečovacích zařízení. Samotná existence GPS nezakládá oprávnění svévolně zasahovat do bezpečného ovládání vozidla nebo jej na dálku odstavit.",
  ]},
  { title: "Pokuty a administrativní úkony", items: [
    "Nájemce odpovídá za přestupky a jiné protiprávní jednání související s užíváním vozidla v rozsahu stanoveném právními předpisy.",
    "Obdrží-li Pronajímatel výzvu, oznámení nebo jiný dokument související s užíváním vozidla, může příslušným orgánům poskytnout nezbytné údaje, pokud pro to existuje právní důvod.",
    "Nájemce uhradí pokuty a jiné částky, za které podle právních předpisů odpovídá. Administrativní poplatek za zpracování přestupku lze účtovat pouze tehdy, je-li jeho výše a důvod transparentně sjednán v nájemní smlouvě nebo v sazebníku, který byl Nájemci zpřístupněn před uzavřením smlouvy.",
  ]},
  { title: "Náhradní vozidlo", items: [
    "Nárok na náhradní vozidlo vzniká pouze tehdy, pokud byl výslovně sjednán v nájemní smlouvě nebo vyplývá z právních předpisů.",
    "Není-li náhradní vozidlo sjednáno, Pronajímatel může podle dostupnosti a okolností nabídnout jiné vozidlo. Podmínky, kategorie a případné náklady se dohodnou individuálně.",
    "Tím nejsou dotčena zákonná práva Nájemce v případě, že Pronajímatel poruší své smluvní povinnosti.",
  ]},
  { title: "Odpovědnost za škodu", items: [
    "Nájemce odpovídá za škodu na vozidle a jeho příslušenství v rozsahu stanoveném právními předpisy a nájemní smlouvou. Neodpovídá za běžné opotřebení vzniklé řádným užíváním.",
    "Při posouzení poškození se přihlíží ke stavu vozidla při předání, jeho stáří, počtu najetých kilometrů a obvyklému opotřebení.",
    "Rozsah a výše škody mohou být doloženy zejména fotodokumentací, servisním rozpočtem, fakturou za opravu nebo odborným posouzením. Náhrada škody nesmí vést k dvojímu odškodnění téže újmy.",
    "Odpovědnost Nájemce se posuzuje také s ohledem na příčinnou souvislost, zavinění a případné zákonné důvody zproštění odpovědnosti.",
  ]},
  { title: "Doba trvání a ukončení nájmu", items: [
    "Nájem se sjednává na dobu určitou nebo neurčitou podle nájemní smlouvy. Doba nájmu, výpovědní doba a případné podmínky předčasného ukončení jsou uvedeny v nájemní smlouvě.",
    "Nájem může skončit uplynutím sjednané doby, dohodou, výpovědí, odstoupením nebo jiným způsobem stanoveným právními předpisy či nájemní smlouvou.",
    "Pronajímatel může smlouvu ukončit nebo od ní odstoupit zejména při podstatném porušení smluvních povinností, například při závažném či opakovaném prodlení s úhradou, neoprávněném přenechání vozidla třetí osobě, užívání vozidla k zakázanému účelu nebo úmyslném poškozování vozidla. Postup a právní následky se vždy řídí nájemní smlouvou a právními předpisy.",
    "Finanční důsledky předčasného ukončení nájmu musí vyplývat z platného a dostatečně určitého smluvního ujednání nebo z právních předpisů. U spotřebitelů se nepoužijí ujednání, která by nepřípustně omezovala jejich zákonná práva.",
  ]},
  { title: "Vrácení vozidla", items: [
    "Nájemce vrátí vozidlo v termínu, místě a způsobem sjednaným v nájemní smlouvě. Vozidlo vrátí ve stavu odpovídajícím stavu při převzetí s přihlédnutím k běžnému opotřebení.",
    "Nájemce vrátí všechny předané klíče, doklady a příslušenství. Při vrácení může být vyhotoven přebírací protokol, který zachytí zejména stav tachometru, paliva nebo nabití, stav vozidla a případná poškození.",
    "Za poškození nad rámec běžného opotřebení odpovídá Nájemce v rozsahu stanoveném právními předpisy a nájemní smlouvou. Mimořádné čištění lze účtovat pouze tehdy, pokud bylo předem transparentně sjednáno a odpovídá sjednanému sazebníku nebo skutečným oprávněným nákladům.",
    "Při prodlení s vrácením vozidla může Pronajímatel požadovat náhradu za další užívání a případnou prokázanou škodu v souladu s právními předpisy.",
  ]},
  { title: "Ochrana osobních údajů", items: [
    "Pronajímatel zpracovává osobní údaje Nájemce v rozsahu nezbytném pro uzavření a plnění smlouvy, plnění právních povinností a ochranu oprávněných zájmů, pokud jsou splněny zákonné podmínky.",
    "Zpracovávané údaje mohou zahrnovat identifikační a kontaktní údaje, smluvní a platební údaje a údaje související s užíváním vozidla. Údaje mohou být v nezbytném rozsahu předány poskytovatelům účetních, právních, servisních a technických služeb, pojišťovnám nebo orgánům veřejné moci, pokud pro předání existuje právní důvod.",
    "Podrobné informace o účelech, právních titulech, příjemcích, době uchovávání a právech subjektů údajů jsou uvedeny v samostatných Zásadách zpracování osobních údajů.",
  ]},
  { title: "Reklamace a spotřebitelské spory", items: [
    "Reklamaci nebo stížnost lze uplatnit prostřednictvím kontaktních údajů Pronajímatele uvedených v těchto VOP nebo na webových stránkách. Nájemce uvede své identifikační údaje, označení smlouvy a popis reklamované skutečnosti.",
    "Pronajímatel reklamaci posoudí a vyřídí v souladu s příslušnými právními předpisy.",
    "Je-li Nájemce spotřebitelem a vznikne-li mezi ním a Pronajímatelem spotřebitelský spor, který se nepodaří vyřešit dohodou, může spotřebitel podat návrh na mimosoudní řešení sporu u České obchodní inspekce: Česká obchodní inspekce, Ústřední inspektorát – oddělení ADR, Gorazdova 1969/24, 120 00 Praha 2, web https://coi.gov.cz/informace-o-adr/.",
  ]},
  { title: "Závěrečná ustanovení", items: [
    "Tyto VOP tvoří součást nájemní smlouvy, pokud na ně nájemní smlouva odkazuje a Nájemce měl možnost se s nimi seznámit před uzavřením smlouvy.",
    "Nové znění VOP se použije na smluvní vztahy vzniklé po nabytí jeho účinnosti. Změny již uzavřených smluv se řídí příslušnými právními předpisy a individuální smlouvou.",
    "Pokud se některé ustanovení ukáže jako neplatné, neúčinné nebo nevymahatelné, nemá to samo o sobě vliv na ostatní ustanovení.",
    "Tyto VOP nabývají účinnosti dnem 30. října 2026.",
  ]},
];

// VOP Page
const VOPPage = () => {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[#050505]">
      <Seo title="Všeobecné obchodní podmínky | EffectDeal" description="Všeobecné obchodní podmínky dlouhodobého pronájmu vozidel EffectDeal." path="/vop" />
      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="/" className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_IMG} alt="EffectDeal" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              <span className="font-['Cabinet_Grotesk'] font-bold text-lg sm:text-xl tracking-wide">EFFECTDEAL</span>
            </a>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <a href="/" className="hidden sm:inline text-sm text-zinc-400 hover:text-white transition-colors">
                {t("common.backHome")}
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-4">
              <FileText className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>Všeobecné obchodní podmínky</span>
            </div>
            <h1 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl font-bold">Všeobecné obchodní podmínky</h1>
            <p className="text-zinc-400 mt-3">Pronájem motorových vozidel</p>
            <div className="mt-5 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/10 border border-orange-500/20 text-orange-300 text-sm" data-testid="vop-effective">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400" />
              Účinné od 30. října 2026
            </div>
          </div>

          {/* Provider identification */}
          <div className="glass p-6 sm:p-8 rounded-2xl mb-6" data-testid="vop-provider">
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-1">Identifikace poskytovatele</h2>
            <p className="font-['Cabinet_Grotesk'] text-xl font-bold mb-4">effectdeal s.r.o.</p>
            <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
              {VOP_PROVIDER.map(([label, value]) => (
                <div key={label} className="flex flex-col">
                  <dt className="text-xs uppercase tracking-wider text-zinc-500">{label}</dt>
                  <dd className="text-zinc-200">{value}</dd>
                </div>
              ))}
            </dl>
            <p className="text-zinc-400 leading-relaxed mt-6 pt-6 border-t border-white/10">
              Tyto všeobecné obchodní podmínky (dále jen „VOP“) upravují smluvní vztahy při pronájmu
              motorových vozidel společností effectdeal s.r.o. (dále jen „Pronajímatel“).
            </p>
            <button
              onClick={() => window.print()}
              className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-orange-500 text-black font-semibold text-sm hover:bg-orange-400 transition-colors no-print"
              data-testid="vop-print-btn"
            >
              <Download className="w-4 h-4" /> Stáhnout / vytisknout (PDF)
            </button>
          </div>

          {/* Table of contents */}
          <div className="glass p-6 sm:p-8 rounded-2xl mb-6 no-print" data-testid="vop-toc">
            <h2 className="text-sm uppercase tracking-widest text-zinc-500 mb-4">Obsah</h2>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {VOP_ARTICLES.map((a, i) => (
                <li key={i}>
                  <a href={`#cl-${i + 1}`} className="flex items-start gap-3 text-zinc-300 hover:text-orange-300 transition-colors group">
                    <span className="text-orange-400/80 font-semibold tabular-nums w-6 shrink-0 group-hover:text-orange-300">{i + 1}.</span>
                    <span className="leading-snug">{a.title}</span>
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {/* Articles */}
          <div className="space-y-6">
            {VOP_ARTICLES.map((a, i) => (
              <section key={i} id={`cl-${i + 1}`} className="glass p-6 sm:p-8 rounded-2xl scroll-mt-28" data-testid={`vop-article-${i + 1}`}>
                <h2 className="flex items-baseline gap-3 font-['Cabinet_Grotesk'] text-xl sm:text-2xl font-bold mb-5">
                  <span className="text-orange-400 tabular-nums">{i + 1}.</span>
                  <span>{a.title}</span>
                </h2>
                <ol className="space-y-4">
                  {a.items.map((text, j) => (
                    <li key={j} className="flex gap-3 sm:gap-4">
                      <span className="text-sm font-semibold text-orange-400/70 tabular-nums shrink-0 mt-0.5 w-8">{i + 1}.{j + 1}</span>
                      <p className="text-zinc-300 leading-relaxed">{text}</p>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </div>

          <p className="text-center text-zinc-500 text-sm mt-10">
            effectdeal s.r.o. · effectdeal.com
          </p>
        </div>
      </main>

      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-zinc-600 text-xs">© {new Date().getFullYear()} EffectDeal. {t("common.rights")}</p>
        </div>
      </footer>
    </div>
  );
};

// GDPR Page
const GDPRPage = () => {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-[#050505]">
      <Seo title="Ochrana osobních údajů (GDPR) | EffectDeal" description="Zásady zpracování a ochrany osobních údajů (GDPR) společnosti EffectDeal." path="/gdpr" />
      <nav className="fixed top-0 left-0 right-0 z-50 nav-glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <a href="/" className="flex items-center gap-2 sm:gap-3">
              <img src={LOGO_IMG} alt="EffectDeal" className="w-8 h-8 sm:w-10 sm:h-10 object-contain" />
              <span className="font-['Cabinet_Grotesk'] font-bold text-lg sm:text-xl tracking-wide">EFFECTDEAL</span>
            </a>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <a href="/" className="hidden sm:inline text-sm text-zinc-400 hover:text-white transition-colors">
                {t("common.backHome")}
              </a>
            </div>
          </div>
        </div>
      </nav>

      <main className="pt-24 pb-16 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 text-orange-400 font-medium tracking-widest uppercase text-xs sm:text-sm mb-4">
              <Shield className="w-4 h-4 sm:w-5 sm:h-5" />
              <span>{t("gdpr.eyebrow")}</span>
            </div>
            <h1 className="font-['Cabinet_Grotesk'] text-3xl sm:text-4xl font-bold">{t("gdpr.title")}</h1>
          </div>

          <div className="glass p-6 sm:p-10 rounded-2xl space-y-6">
            <p className="text-zinc-400 leading-relaxed">{t("gdpr.intro")}</p>

            <div>
              <h3 className="text-lg font-bold text-white mb-4">{t("gdpr.purposeTitle")}</h3>
              <div className="space-y-3">
                {["gdpr.purpose1", "gdpr.purpose2", "gdpr.purpose3"].map((k) => (
                  <div key={k} className="flex items-center gap-3 p-4 rounded-xl bg-white/5">
                    <CheckCircle2 className="w-5 h-5 text-orange-500 flex-shrink-0" />
                    <p className="text-zinc-300">{t(k)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4 pt-4">
              <div className="p-4 rounded-xl bg-white/5">
                <p className="text-zinc-300 leading-relaxed">{t("gdpr.p1")}</p>
              </div>
              <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                <p className="text-zinc-300 leading-relaxed">
                  <strong className="text-white">{t("gdpr.rightsLabel")}</strong> {t("gdpr.rights")}
                </p>
              </div>
            </div>

            <div className="pt-4">
              <p className="text-zinc-500 text-sm">
                {t("gdpr.contactPre")}{' '}
                <a href="mailto:marketing@effectdeal.com" className="text-orange-400 hover:underline">marketing@effectdeal.com</a>
              </p>
            </div>
          </div>
        </div>
      </main>

      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <p className="text-zinc-600 text-xs">© {new Date().getFullYear()} EffectDeal. {t("common.rights")}</p>
        </div>
      </footer>
    </div>
  );
};

// Floating WhatsApp button
const WhatsAppButton = () => (
  <a
    href="https://wa.me/420725706235?text=Dobr%C3%BD%20den%2C%20m%C3%A1m%20z%C3%A1jem%20o%20pron%C3%A1jem%20vozidla."
    target="_blank"
    rel="noopener noreferrer"
    aria-label="Napište nám na WhatsApp"
    data-testid="whatsapp-float-btn"
    className="fixed bottom-5 right-24 z-50 w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-black/30 hidden sm:flex items-center justify-center hover:scale-110 active:scale-95 transition-transform duration-200"
  >
    <svg viewBox="0 0 32 32" className="w-7 h-7 fill-white" aria-hidden="true">
      <path d="M16.003 3C9.383 3 4 8.383 4 15.003c0 2.117.553 4.184 1.604 6.01L4 29l8.18-1.57a11.94 11.94 0 0 0 3.823.627h.003C22.63 28.057 28 22.674 28 16.054 28 12.84 26.75 9.82 24.48 7.55A11.93 11.93 0 0 0 16.003 3zm0 2.18a9.78 9.78 0 0 1 6.96 2.886 9.74 9.74 0 0 1 2.86 6.99c0 5.42-4.41 9.83-9.84 9.83a9.8 9.8 0 0 1-3.49-.64l-.25-.1-4.85.93.93-4.73-.16-.25a9.76 9.76 0 0 1-1.49-5.2c0-5.42 4.41-9.83 9.83-9.83zm-3.43 5.27c-.16 0-.42.06-.64.3-.22.24-.84.82-.84 2 0 1.18.86 2.32.98 2.48.12.16 1.7 2.6 4.13 3.64.58.25 1.03.4 1.38.51.58.18 1.1.16 1.52.1.46-.07 1.42-.58 1.62-1.14.2-.56.2-1.04.14-1.14-.06-.1-.22-.16-.46-.28-.24-.12-1.42-.7-1.64-.78-.22-.08-.38-.12-.54.12-.16.24-.62.78-.76.94-.14.16-.28.18-.52.06-.24-.12-1.02-.38-1.94-1.2-.72-.64-1.2-1.43-1.34-1.67-.14-.24-.02-.37.1-.49.11-.11.24-.28.36-.42.12-.14.16-.24.24-.4.08-.16.04-.3-.02-.42-.06-.12-.54-1.3-.74-1.78-.2-.47-.4-.4-.54-.41z"/>
    </svg>
  </a>
);

// Main Home Component
const Home = () => {
  useReveal();
  return (
    <div className="min-h-screen bg-[#050505]">
      <Seo
        title="EffectDeal | Dlouhodobý pronájem vozidel po celé ČR"
        description="Dlouhodobý pronájem vozidel od 1 měsíce. Pojištění, dálniční známka a servis v ceně. Vozy pro firmy i jednotlivce po celé ČR. Nezávazná poptávka online."
        path="/"
      />
      <Navigation />
      <HeroSection />
      <VehiclesGallery />
      <ValueStrip />
      <HowItWorksSection />
      <WhyEffectDealSection />
      <ComparisonSection />
      <ReviewsSection />
      <PriceCalculator />
      <FaqSection />
      <ContactSection />
      <ReferralSection />
      <Footer />
      <WhatsAppButton />
      <StickyMobileCTA />
      <ChatWidget />
      <ExitIntentModal />
    </div>);
};

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <VehiclesProvider>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/spoluprace" element={<SpolupracePage />} />
              <Route path="/kariera" element={<KarieraPage />} />
              <Route path="/vozy/:slug" element={<VehicleDetail />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/vop" element={<VOPPage />} />
              <Route path="/gdpr" element={<GDPRPage />} />
              <Route path="/prihlaseni" element={<LoginPage />} />
              <Route path="/nastavit-heslo" element={<SetPasswordPage />} />
              <Route path="/podpis-spoluprace/:token" element={<CooperationSignPage />} />
              <Route path="/admin" element={<ProtectedRoute role="admin"><AdminDashboard /></ProtectedRoute>} />
              <Route path="/prace" element={<ProtectedRoute role="work"><WorkerPage /></ProtectedRoute>} />
              <Route path="/flotila/prihlaseni" element={<FlotilaLoginPage />} />
              <Route path="/flotila/:slug" element={<FlotilaLoginPage />} />
              <Route path="/flotila" element={<ProtectedRoute role="fleet" loginPath="/flotila/prihlaseni"><FlotilaPage /></ProtectedRoute>} />
              <Route path="/klient" element={<ProtectedRoute role="client"><ClientPortal /></ProtectedRoute>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
            <CookieConsent />
            </VehiclesProvider>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
      <ConfirmRoot />
      <Toaster theme="dark" position="top-center" richColors closeButton />
    </div>);
}

export default App;
